/**
 * COMPONENT_SPEC §6.7 `MeasurementAuthoringForm` — SCR-MEASNEW.
 * OWNER: web-frontend-engineer.
 *
 * MASTER TAILOR ONLY. CREATE ONLY. There is no edit view, and no update or
 * delete endpoint exists at any path for any role (D-009, P-MEAS-06).
 *
 * THE CONSTRUCTION RULE THAT GOVERNS EVERYTHING HERE:
 *
 *   This component and `MeasurementRecord` are DIFFERENT COMPONENTS IN
 *   DIFFERENT MODULES and MUST NOT SHARE A LAYOUT (RULING-001 consequences,
 *   FLOWS_SPEC §10.6). This file imports nothing from `./record.tsx` and
 *   `./record.tsx` imports nothing from here. Reusing the record's layout here
 *   — or this form's layout there — is the exact engineering convenience
 *   RULING-001 refused, purchased with the most important idea in the product.
 *
 * Its values are NEVER pre-filled from the previous record: pre-filling would
 * make a remeasure a diff of the old numbers rather than a fresh reading of the
 * body.
 */

import { useState } from 'react';
import { t, tDynamic, type CopyKey } from '@pt/copy';
import {
  MEASUREMENT_FIELDS,
  MEASUREMENT_GROUPS,
  inchesToCm,
  measurementGroupCopyKey,
} from '@pt/view';
import { decimal, joinList } from '@pt/view';
import { Button, ErrorStatement, FactLine, RovingRadioGroup, Statement, TextInput } from './primitives.tsx';

const REASONS = ['initial', 'remeasure', 'correction', 'post_fitting_adjustment', 'legacy_import'] as const;

export interface AuthoringSubmission {
  readonly takenAt: string;
  readonly takenAtPlace: string;
  readonly method: string;
  readonly reason: string;
  readonly supersedes: string | null;
  readonly values: Record<string, number | string>;
  readonly notes?: string;
}

export interface MeasurementAuthoringFormProps {
  readonly clientName: string;
  readonly supersedes: string | null;
  readonly previousTakenAt: string | null;
  readonly busy: boolean;
  readonly onSaveDraft: (submission: AuthoringSubmission) => void;
  readonly onLock: (submission: AuthoringSubmission) => void;
  readonly refusal?: React.ReactNode;
  readonly draftSaved?: boolean;
}

export function MeasurementAuthoringForm({
  clientName,
  supersedes,
  previousTakenAt,
  busy,
  onSaveDraft,
  onLock,
  refusal,
  draftSaved = false,
}: MeasurementAuthoringFormProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [entryUnits, setEntryUnits] = useState<'cm' | 'in'>('cm');
  const [takenAt, setTakenAt] = useState('');
  const [place, setPlace] = useState('');
  const [reason, setReason] = useState<string>(supersedes === null ? 'initial' : 'remeasure');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState<readonly string[]>([]);
  const [confirming, setConfirming] = useState(false);

  const cmOf = (fieldId: string): number | null => {
    const raw = values[fieldId];
    if (raw === undefined || raw.trim() === '') return null;
    const parsed = Number.parseFloat(raw);
    if (Number.isNaN(parsed)) return null;
    const field = MEASUREMENT_FIELDS.find((f) => f.id === fieldId);
    if (field?.unit === 'cm' && entryUnits === 'in') return inchesToCm(parsed);
    return parsed;
  };

  /** `shoulder_drop_side != none` → `shoulder_drop_cm` is required, and only then. */
  const conditionalShoulderDrop = (values['shoulder_drop_side'] ?? 'none') !== 'none';

  function collect(): AuthoringSubmission {
    const out: Record<string, number | string> = {};
    for (const field of MEASUREMENT_FIELDS) {
      const raw = values[field.id];
      if (raw === undefined || raw.trim() === '') continue;
      if (field.type === 'number') {
        const cm = cmOf(field.id);
        if (cm !== null) out[field.id] = cm;
      } else {
        out[field.id] = raw;
      }
    }
    return {
      takenAt,
      takenAtPlace: place,
      method: 'in_person',
      reason,
      supersedes,
      values: out,
    };
  }

  function validate(): boolean {
    const nextErrors: Record<string, string> = {};
    const nextMissing: string[] = [];
    for (const field of MEASUREMENT_FIELDS) {
      const raw = values[field.id];
      const empty = raw === undefined || raw.trim() === '';
      const required =
        field.required || (field.id === 'shoulder_drop_cm' && conditionalShoulderDrop);
      if (empty) {
        if (required) nextMissing.push(t(`meas.field.${field.id}.label` as CopyKey));
        continue;
      }
      if (field.type === 'number') {
        const cm = cmOf(field.id);
        if (cm === null || (field.min !== null && cm < field.min) || (field.max !== null && cm > field.max)) {
          // ST-MEASNEW-RANGE — names the permitted range. NEVER silently clamped.
          nextErrors[field.id] = t('measnew.range', {
            fieldLabel: t(`meas.field.${field.id}.label` as CopyKey),
            min: field.min == null ? '' : decimal(field.min, 1),
            max: field.max == null ? '' : decimal(field.max, 1),
            unit: field.unit ?? '',
          });
        }
      }
    }
    setErrors(nextErrors);
    setMissing(nextMissing);
    return Object.keys(nextErrors).length === 0 && nextMissing.length === 0;
  }

  return (
    <div>
      <h1 className="type-role-page-title" id="pt-h1" tabIndex={-1}>
        {supersedes === null
          ? t('measnew.heading.initial', { clientNameStaff: clientName })
          : t('measnew.heading.superseding', { clientNameStaff: clientName })}
      </h1>

      {/* ST-MEASNEW-SUPERSEDING — stated BEFORE anything is entered. */}
      {supersedes === null || previousTakenAt == null ? null : (
        <Statement>
          {t('measnew.superseding.statement', { previousTakenAt: previousTakenAt })}
        </Statement>
      )}

      {refusal}
      {draftSaved ? <Statement>{t('measnew.draft.statement')}</Statement> : null}

      <TextInput label={t('measnew.takenAt.label')} type="date" value={takenAt} onChange={setTakenAt} />
      <TextInput label={t('el.provenance.method.in_person')} value={place} onChange={setPlace} />

      <fieldset className="pt-filter pt-block">
        <legend className="pt-filter__legend">{t('measnew.reason.label')}</legend>
        <RovingRadioGroup
          ariaLabel={t('measnew.reason.label')}
          values={REASONS}
          chosen={reason}
          labelOf={(id) => tDynamic(`measnew.reason.${id}`)}
          onChoose={setReason}
        />
      </fieldset>

      <fieldset className="pt-filter pt-block">
        <legend className="pt-filter__legend">{t('measnew.units.label')}</legend>
        <RovingRadioGroup
          ariaLabel={t('measnew.units.label')}
          values={['cm', 'in']}
          chosen={entryUnits}
          labelOf={(unit) => (unit === 'cm' ? t('meas.units.cm') : t('meas.units.in'))}
          onChoose={(unit) => setEntryUnits(unit as 'cm' | 'in')}
        />
      </fieldset>

      {MEASUREMENT_GROUPS.map((group) => {
        const fields = MEASUREMENT_FIELDS.filter((f) => f.group === group.id).filter(
          (f) => f.id !== 'shoulder_drop_cm' || conditionalShoulderDrop,
        );
        if (fields.length === 0) return null;
        const headingKey = measurementGroupCopyKey(group.id);
        return (
          <section className="pt-section" key={group.id}>
            <h2 className="pt-record__groupHeading">
              {headingKey === undefined ? t('meas.notes.heading', { authorName: clientName }) : t(headingKey)}
            </h2>
            {fields.map((field) => {
              const label = t(`meas.field.${field.id}.label` as CopyKey);
              const value = values[field.id] ?? '';
              if (field.type === 'enum' && field.values !== null) {
                return (
                  <fieldset className="pt-filter pt-block" key={field.id}>
                    <legend className="pt-filter__legend">{label}</legend>
                    <RovingRadioGroup
                      ariaLabel={label}
                      values={field.values}
                      chosen={value}
                      labelOf={(option) => tDynamic(`meas.value.${field.id}.${option}`)}
                      onChoose={(option) => setValues((v) => ({ ...v, [field.id]: option }))}
                    />
                  </fieldset>
                );
              }
              const cm = cmOf(field.id);
              return (
                <div key={field.id}>
                  <TextInput
                    label={label}
                    value={value}
                    multiline={field.type === 'text'}
                    inputMode={field.type === 'number' ? 'decimal' : 'text'}
                    hint={
                      field.type === 'number' && field.min !== null && field.max !== null
                        ? t('measnew.range', {
                            fieldLabel: label,
                            min: decimal(field.min, 1),
                            max: decimal(field.max, 1),
                            unit: field.unit ?? '',
                          })
                        : undefined
                    }
                    {...(errors[field.id] === undefined ? {} : { error: errors[field.id] as string })}
                    onChange={(next) => setValues((v) => ({ ...v, [field.id]: next }))}
                  />
                  {/* An inch entry is converted ONCE, on input, and the tailor
                      sees the converted centimetre value before locking. */}
                  {entryUnits === 'in' && field.unit === 'cm' && cm !== null ? (
                    <FactLine>{t('measnew.inchNote', { cmValue: decimal(cm, 1) })}</FactLine>
                  ) : null}
                </div>
              );
            })}
          </section>
        );
      })}

      {conditionalShoulderDrop ? (
        <Statement>{t('measnew.conditional.shoulderDrop')}</Statement>
      ) : null}

      {missing.length === 0 ? null : (
        <ErrorStatement
          what={t('measnew.required', { fieldLabel: missing[0] as string })}
          means={
            missing.length > 1
              ? t('measnew.required.remaining', {
                  remainingCount: missing.length - 1,
                  remainingList: joinList(missing.slice(1)),
                })
              : undefined
          }
        />
      )}

      <div className="pt-actions pt-actions--equal">
        <Button onClick={() => onSaveDraft(collect())}>{t('measnew.draft.statement')}</Button>
        <Button
          variant="primary"
          onClick={() => {
            if (validate()) setConfirming(true);
          }}
        >
          {t('measnew.lock.action')}
        </Button>
      </div>

      {/* ST-MEASNEW-LOCK — a confirm step stating plainly that once locked no
          field may ever change, by anyone, including the tailor himself. */}
      {confirming ? (
        <section className="pt-confirmstep" role="group" aria-label={t('measnew.lock.action')}>
          <h2 className="pt-cutsheet__blockTitle">{t('measnew.lock.action')}</h2>
          <Statement>{t('measnew.lock.statement')}</Statement>
          <div className="pt-actions pt-actions--equal">
            <Button busy={busy} busyLabel={t('trans.busy')} onClick={() => onLock(collect())}>
              {t('measnew.lock.confirm')}
            </Button>
            <Button onClick={() => setConfirming(false)}>{t('global.action.back')}</Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
